/**
 * Basic Info Section for Project Part Detail Page
 * Displays title, label/category, kostenplaatsen (from budget), and description
 */

import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tag, Cube, CurrencyEur, Info, Plus, Ruler } from "@phosphor-icons/react";
import { ProjectPart, RenovationLabel } from "shared/types/renovation";
import { LabelSearch } from "shared/components/labelSearch/labelSearch";
import { BudgetLinePickerModal } from "../BudgetLinePickerModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PartBasicInfoSectionProps {
  part: ProjectPart;
  onFieldChange: (field: string, value: unknown) => void;
  /** Building id — needed to drive the budget link flow. */
  buildingId?: string;
  /** Refresh callback after the budget link flow saves. */
  onUpdate?: () => void;
}

export const PartBasicInfoSection: React.FC<PartBasicInfoSectionProps> = ({
  part,
  onFieldChange,
  buildingId,
  onUpdate,
}) => {
  const [isAddBudgetOpen, setIsAddBudgetOpen] = useState(false);
  // Calculate total budget from investment item assignments
  const totalBudget = useMemo(() => {
    let total = 0;
    if ((part as any).investmentAssignments) {
      (part as any).investmentAssignments.forEach((assignment: any) => {
        total += Number(assignment.assignedAmount || 0);
      });
    }
    return total;
  }, [part]);

  // Get unique units from investment assignments with accurate budget distribution
  // Uses unitDistributions if available, otherwise falls back to equal split
  const linkedUnits = useMemo(() => {
    const unitsMap = new Map<string, {
      id: string;
      budgetAmount: number;
      surface: number;
    }>();

    // Get units from investment item part assignments
    if ((part as any).investmentAssignments) {
      (part as any).investmentAssignments.forEach((assignment: any) => {
        const assignedAmount = Number(assignment.assignedAmount || 0);

        // Check if this assignment has unit distributions
        if (assignment.unitDistributions?.length > 0) {
          // Use the exact distribution amounts
          assignment.unitDistributions.forEach((dist: any) => {
            if (dist.unit) {
              const unitId = dist.unit.id;
              const amount = Number(dist.amount || 0);
              const surface = dist.unit.netSurface || dist.unit.grossSurface || 0;
              const existing = unitsMap.get(unitId);

              unitsMap.set(unitId, {
                id: unitId,
                budgetAmount: (existing?.budgetAmount || 0) + amount,
                surface: existing?.surface || surface,
              });
            }
          });
        } else if (assignment.investmentItem?.units?.length > 0) {
          // Fallback: distribute equally across all units on the investment item
          const units = assignment.investmentItem.units;
          const amountPerUnit = assignedAmount / units.length;

          units.forEach((unit: any) => {
            const existing = unitsMap.get(unit.id);
            const surface = unit.netSurface || unit.grossSurface || 0;

            unitsMap.set(unit.id, {
              id: unit.id,
              budgetAmount: (existing?.budgetAmount || 0) + amountPerUnit,
              surface: existing?.surface || surface,
            });
          });
        }
      });
    }

    return Array.from(unitsMap.values());
  }, [part]);

  // Format currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("nl-BE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-4 border-b border-neutral-100 pb-2">
          <h4 className="text-sm font-medium text-neutral-700">
            Basis informatie
          </h4>
        </div>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="part-title">Titel</Label>
            <Input
              id="part-title"
              value={part.title || ""}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder="Titel van het onderdeel..."
            />
          </div>

          {/* Category/Label - Made more prominent */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag size={14} weight="duotone" className="text-moss-primary" />
              Categorie
            </Label>
            <div className="relative">
              <LabelSearch
                value={part.label || null}
                onChange={(label: RenovationLabel | null) =>
                  onFieldChange("label", label)
                }
                placeholder="Selecteer een categorie..."
              />
              {!part.label && (
                <p className="mt-1 text-xs text-amber-600">
                  Een categorie helpt bij het koppelen van budgetten en leveranciers
                </p>
              )}
            </div>
          </div>

          {/* Kostenplaatsen - Read-only display from budget assignments */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Cube size={14} weight="duotone" className="text-moss-primary" />
              Kostenplaatsen
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={14} className="cursor-help text-neutral-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Kostenplaatsen worden automatisch gekoppeld via budgettoewijzingen.
                    Wijs budget toe om kostenplaatsen te koppelen.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>

            {linkedUnits.length > 0 ? (
              <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                <div className="space-y-2">
                  {linkedUnits.map((unit) => {
                    const percentage = totalBudget > 0
                      ? Math.round((unit.budgetAmount / totalBudget) * 100)
                      : 0;
                    const eurPerM2 = unit.surface > 0 ? unit.budgetAmount / unit.surface : 0;

                    return (
                      <div
                        key={unit.id}
                        className="flex items-center justify-between rounded-md border border-moss-primary/20 bg-moss-primary/5 px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <Cube size={14} className="text-moss-primary" />
                          <span className="text-sm font-medium text-moss-primary">
                            {unit.id}
                          </span>
                          {unit.surface > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-neutral-500">
                              <Ruler size={10} />
                              {unit.surface.toFixed(0)} m²
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {unit.budgetAmount > 0 && (
                            <>
                              <Badge
                                variant="outline"
                                className="h-5 border-moss-primary/30 bg-white px-1.5 text-[10px] text-moss-primary"
                              >
                                <CurrencyEur size={10} className="mr-0.5" />
                                {formatCurrency(unit.budgetAmount)}
                                <span className="ml-1 text-neutral-500">({percentage}%)</span>
                              </Badge>
                              {unit.surface > 0 && eurPerM2 > 0 && (
                                <Badge
                                  variant="outline"
                                  className="h-5 border-neutral-300 bg-white px-1.5 text-[10px] text-neutral-600"
                                >
                                  {formatCurrency(eurPerM2)}/m²
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-neutral-500">
                  {linkedUnits.length} kostenplaats(en) gekoppeld via budgettoewijzing
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="mt-0.5 flex-shrink-0 text-neutral-400" />
                    <p className="text-sm text-neutral-600">
                      Geen kostenplaatsen gekoppeld
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setIsAddBudgetOpen(true)}
                    disabled={!buildingId}
                    className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Budget koppelen
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="part-description">Beschrijving</Label>
            <Textarea
              id="part-description"
              value={part.description || ""}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Beschrijving van het onderdeel..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Budget detail picker — table of every investment line on the
          building. Triggered from the "Budget koppelen" empty-state button
          above; lets the user pick one or more lines and take budget. */}
      {buildingId && (
        <BudgetLinePickerModal
          part={part}
          buildingId={buildingId}
          isOpen={isAddBudgetOpen}
          onClose={() => setIsAddBudgetOpen(false)}
          onSuccess={() => {
            setIsAddBudgetOpen(false);
            onUpdate?.();
          }}
        />
      )}
    </TooltipProvider>
  );
};

export default PartBasicInfoSection;
